import { createSelector } from 'reselect';

import get from 'lodash/get';

import prop from 'lodash/fp/prop';
import filter from 'lodash/fp/filter';
import pipe from 'lodash/fp/pipe';
import map from 'lodash/fp/map';
import pick from 'lodash/fp/pick';
import keyBy from 'lodash/fp/keyBy';
import groupBy from 'lodash/fp/groupBy';
import flatten from 'lodash/flatten';

import { getResult, hasResults, mergeMatches } from '@utils/match';
import {
  selectGroupWinners as groupsWinnersSelector,
  selectBracketMatches,
} from '@selectors/matches';

import { Stage, Round, MatchProps } from '@models/Match';

// Sistema de Puntuación v2: Nuevas assertions
const Assertions = {
  // Sistema v2 - Fase de Grupos
  ResultadoCorrecto: 'resultado-correcto',
  ResultadoExacto: 'resultado-exacto',
  
  // Sistema v2 - Fase Eliminatoria
  EquipoClasificado: 'equipo-clasificado',
  ResultadoExactoEliminatoria: 'resultado-exacto-eliminatoria',

  // Legacy - Sistema v1 (mantener por compatibilidad temporal)
  GroupsWinner: 'groups-winner',
  GroupsScore: 'groups-score',
  BracketWinner: 'bracket-winner',
  BracketScore: 'bracket-score',
  ExtraWinner: 'extra-winner',
  ExtraScore: 'extra-score',
  TeamGroup: 'team-group',
  GroupTop: 'group-top',
  TeamQuarter: 'team-quarter',
  TeamSemi: 'team-semi',
  TeamFinal: 'team-final',
  Champion: 'champion',
  TimesGoalScorer: 'times-goals-scorer',
};

const createGetAssert =
  (points, assertions = []) =>
  (code) =>
    assertions.includes(code) ? points[code] : 0;

const calculateGroupMatches = (points) =>
  map(({ assertions, ...match }) => {
    let getAssert = createGetAssert(points, assertions);

    // Sistema v2: mapear nuevas assertions
    const resultadoCorrecto = getAssert(Assertions.ResultadoCorrecto);
    const resultadoExacto = getAssert(Assertions.ResultadoExacto);
    
    // Legacy: mantener compatibilidad con sistema v1
    const legacyWinner = getAssert(Assertions.GroupsWinner);
    const legacyScore = getAssert(Assertions.GroupsScore);

    return {
      ...match,
      result: resultadoCorrecto || legacyWinner, // v2 o v1
      exact: resultadoExacto || legacyScore, // v2 o v1
      extra: getAssert(Assertions.ExtraWinner),
      full: getAssert(Assertions.ExtraScore),
    };
  });

const calculateBracketMatches = (points) =>
  map(({ assertions, ...match }) => {
    let getAssert = createGetAssert(points, assertions);

    // Sistema v2: mapear nuevas assertions de eliminatorias
    const equipoClasificado = getAssert(Assertions.EquipoClasificado);
    const resultadoExactoEliminatoria = getAssert(Assertions.ResultadoExactoEliminatoria);
    
    // Legacy: mantener compatibilidad con sistema v1
    const legacyWinner = getAssert(Assertions.BracketWinner);
    const legacyScore = getAssert(Assertions.BracketScore);

    return {
      ...match,
      result: equipoClasificado || legacyWinner, // v2 o v1
      exact: resultadoExactoEliminatoria || legacyScore, // v2 o v1
      extra: getAssert(Assertions.ExtraWinner),
      full: getAssert(Assertions.ExtraScore),
    };
  });

const sumTotal = map((row) => ({
  ...row,
  total: row.result + (row.exact || 0) + (row.extra || 0) + (row.full || 0),
}));

export const selectGroupMatches = createSelector(
  [prop('matches'), prop('scores.matches'), prop('config.points')],
  (matches, scores, points) =>
    pipe(
      filter({ stage: Stage.Groups }),
      map(pick([MatchProps.MatchId, MatchProps.TeamHome, MatchProps.TeamAway])),
      // Merge solo assertions, NO equipos
      (realMatches) => realMatches.map(match => {
        const scoreMatch = (scores || []).find(s => s.mid === match.mid);
        return {
          ...match,
          assertions: scoreMatch ? scoreMatch.assertions : []
        };
      }),
      calculateGroupMatches(points),
      sumTotal
    )(matches)
);

export const selectGroupMatchesGrouped = createSelector(
  [prop('matches'), prop('scores.matches'), prop('config.points')],
  (matches, scores, points) =>
    pipe(
      filter({ stage: Stage.Groups }),
      map((m) => ({ ...m, hasResults: hasResults(m) })),
      map(
        pick([
          MatchProps.MatchId,
          MatchProps.TeamHome,
          MatchProps.TeamAway,
          MatchProps.Group,
          MatchProps.Datetime,
          'hasResults',
        ])
      ),
      // Merge solo assertions, NO equipos (los equipos ya son los reales del fixture)
      (realMatches) => realMatches.map(match => {
        const scoreMatch = (scores || []).find(s => s.mid === match.mid);
        return {
          ...match,
          assertions: scoreMatch ? scoreMatch.assertions : []
        };
      }),
      calculateGroupMatches(points),
      sumTotal,
      groupBy(MatchProps.Group)
    )(matches)
);

export const selectBracketMatchesByRound = createSelector(
  [selectBracketMatches, prop('scores.matches'), prop('config.points')],
  (matches, scores, points) => {
    return pipe(
      filter({ stage: Stage.Bracket }),
      map((m) => ({ ...m, hasResults: hasResults(m) })),
      map(
        pick([
          MatchProps.MatchId,
          MatchProps.TeamHome,
          MatchProps.TeamAway,
          MatchProps.Round,
          MatchProps.Datetime,
          'hasResults',
        ])
      ),
      // Merge solo assertions, NO equipos (los equipos ya son los reales clasificados)
      (realMatches) => realMatches.map(match => {
        const scoreMatch = (scores || []).find(s => s.mid === match.mid);
        return {
          ...match,
          assertions: scoreMatch ? scoreMatch.assertions : []
        };
      }),
      calculateBracketMatches(points),
      sumTotal,
      groupBy('round')
    )(matches);
  }
);

const calculateGroupsWinners = (winners, scores, points) => {
  let scoreGroups = keyBy('group', scores);

  const createGetTeam = (teams, asserts) => (pos) => ({
    team: teams[pos - 1],
    result: false,
    exact: false,
    extra: asserts.includes(`${Assertions.TeamGroup}:${teams[pos - 1]}`)
      ? points[Assertions.TeamGroup]
      : 0,
    full: asserts.includes(`${Assertions.GroupTop}:${pos}`)
      ? points[Assertions.GroupTop]
      : 0,
  });

  return Object.keys(winners).map((group) => {
    let getTeam = createGetTeam(
      winners[group],
      get(scoreGroups, `${group}.assertions`, [])
    );
    return [getTeam(1), getTeam(2)];
  });
};

export const selectGroupWinners = createSelector(
  [groupsWinnersSelector, prop('scores.groups'), prop('config.points')],
  pipe(calculateGroupsWinners, flatten, sumTotal)
);

const extractTeamsFromBracket = (matches) =>
  matches.reduce(
    (all, match) => [
      ...all,
      { team: match.teamHome, round: match.round },
      { team: match.teamAway, round: match.round },
    ],
    []
  );

const calculateBracketTeamWinners = (scores, points) =>
  map(({ team, round }) => ({
    team,
    round,
    result: false,
    exact: false,
    extra: (scores || []).some((match) =>
      get(match, 'assertions', []).includes(`team-${round}:${team}`)
    )
      ? points[`team-${round}`]
      : 0,
    full: false,
  }));

export const selectBracketWinnersByRound = createSelector(
  [selectBracketMatches, prop('scores.matches'), prop('config.points')],
  (matches, scores, points) =>
    pipe(
      filter(
        ({ stage, round }) =>
          stage === Stage.Bracket &&
          round !== Round.Roundof32 &&
          round !== Round.Roundof16 &&
          round !== Round.Third
      ),
      extractTeamsFromBracket,
      calculateBracketTeamWinners(scores, points),
      sumTotal,
      groupBy('round')
    )(matches)
);

const calculateChampion = (scores, points) => (match) => {
  if (!match) {
    return {
      team: '',
      result: false,
      exact: false,
      extra: 0,
      full: false,
      total: 0,
    };
  }

  let champion = get(getResult(match), 'winner.team', '');
  const byMid = keyBy(MatchProps.MatchId, scores);

  let extra = !champion
    ? 0
    : get(byMid, `[${match.mid}].assertions`, []).includes(Assertions.Champion)
    ? points[Assertions.Champion]
    : 0;

  return {
    team: champion,
    result: false,
    exact: false,
    extra,
    full: false,
    total: extra,
  };
};

export const selectChampion = createSelector(
  [selectBracketMatches, prop('scores.matches'), prop('config.points')],
  (matches, scores, points) => {
    const byId = matches;
    const finalMatch =
      Object.values(byId).find(
        (m) => m && m.stage === Stage.Bracket && m.round === Round.Final
      ) ||
      byId['104'] ||
      byId[104] ||
      byId['64'] ||
      byId[64];
    return calculateChampion(scores, points)(finalMatch);
  }
);

export const selectScorer = createSelector(
  [prop('scores.scorer'), prop('config.points'), prop('config.scorerName')],
  (scorer, points, scorerName) => {
    let goals = get(scorer, 'goals', 0) * points[Assertions.TimesGoalScorer];
    let double =
      scorerName && get(scorer, 'fullname') === scorerName ? goals : 0;

    return {
      scorer: get(scorer, 'fullname', ''),
      result: false,
      exact: false,
      extra: false,
      full: false,
      goals,
      double,
      total: goals + double,
    };
  }
);
